import React, { useEffect, useState } from 'react';
import { UserRole } from '../../types/roles';
import AdminStats from './AdminStats';
import AdminCalendar from './AdminCalendar';
import AdminFinancials from './AdminFinancials';
import AdminEcoCashOperations from './AdminEcoCashOperations';
import { 
  Plus, Trash2, Search, Users, BookOpen, 
  Activity, UserPlus, 
  MoreVertical, Clock, CheckCircle, AlertCircle, TrendingUp,
  Zap, Eye, Edit3, RefreshCw, BarChart3
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { useThemeStore } from '../../stores/themeStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/adminService';
import AddUserModal from './AddUserModal';
import AddSubjectModal from './AddSubjectModal';
import { AdminProgressOverview } from '../progress';

interface AdminDashboardProps {
  userRole: UserRole;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ userRole }) => {
  const { user } = useAuthStore();
  const theme = useThemeStore((state) => state.theme);
  const [showProgressAnalytics, setShowProgressAnalytics] = useState(false);

  useEffect(() => {
    useThemeStore.getState().setIsAdmin(true);
  }, []);

  if (!user?.isAuthenticated) {
    return null;
  }

  const userFullName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : 'Admin';

  const isDark = theme === 'dark';

  return (
    <div className="relative">
      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl animate-pulse transition-colors ${
          isDark ? 'bg-cyan-500/10' : 'bg-cyan-400/20'
        }`} />
        <div className={`absolute top-1/2 -left-40 w-96 h-96 rounded-full blur-3xl animate-pulse transition-colors ${
          isDark ? 'bg-blue-500/10' : 'bg-blue-400/20'
        }`} style={{ animationDelay: '1s' }} />
        <div className={`absolute -bottom-40 right-1/3 w-96 h-96 rounded-full blur-3xl animate-pulse transition-colors ${
          isDark ? 'bg-purple-500/10' : 'bg-purple-400/20'
        }`} style={{ animationDelay: '2s' }} />
      </div>

      {/* Main Content */}
      <div className="relative z-10 p-6 lg:p-8 space-y-8">
        {/* Welcome Text */}
        <div>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Welcome back, {userFullName}. Here's what's happening today.
          </p>
        </div>

        {/* Stats Cards */}
        <AdminStats />

        {/* Financial Overview */}
        <AdminFinancials />

        {/* Main Grid: Activity | Calendar | Management */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ActivityCard isDark={isDark} />
          <AdminCalendar isDark={isDark} />
          <ManagementCard isDark={isDark} />
        </div>

        <AdminEcoCashOperations isDark={isDark} />

        {/* Quick Actions Bar */}
        <div className={`p-4 rounded-2xl border transition-colors ${
          isDark 
            ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/20' 
            : 'bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200'
        }`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'
              }`}>
                <Zap className={isDark ? 'w-5 h-5 text-cyan-400' : 'w-5 h-5 text-cyan-600'} />
              </div>
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Quick Actions</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Manage your platform efficiently</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setShowProgressAnalytics(true)}
                className={`px-4 py-2 rounded-xl border text-sm transition-all flex items-center gap-2 ${
                  isDark 
                    ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white' 
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Progress Analytics
              </button>
              <button className={`px-4 py-2 rounded-xl border text-sm transition-all flex items-center gap-2 ${
                isDark 
                  ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white' 
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}>
                <RefreshCw className="w-4 h-4" />
                Sync Data
              </button>
              <button className={`px-4 py-2 rounded-xl border text-sm transition-all flex items-center gap-2 ${
                isDark 
                  ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white' 
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800'
              }`}>
                <Eye className="w-4 h-4" />
                View Reports
              </button>
              <button className="px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-sm text-white font-medium hover:opacity-90 transition-all flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Add New User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Analytics Modal */}
      {showProgressAnalytics && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowProgressAnalytics(false)}
          />
          <div className={`relative rounded-2xl shadow-2xl border w-full max-w-5xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200 ${
            isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200'
          }`}>
            <div className="h-full max-h-[90vh] overflow-y-auto p-6">
              <AdminProgressOverview onClose={() => setShowProgressAnalytics(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Activity Card ───────────────────────────────────────────────────

const ActivityCard: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const activities = [
    { type: 'success', icon: CheckCircle, text: 'New user registered', user: 'Keith Satuku', time: '2m ago', color: 'text-emerald-400 bg-emerald-500/10' },
    { type: 'info', icon: BookOpen, text: 'Subject updated', user: 'Mathematics Form 3', time: '1h ago', color: 'text-blue-400 bg-blue-500/10' },
    { type: 'warning', icon: AlertCircle, text: 'System update', user: 'v2.4.1 deployed', time: '3h ago', color: 'text-amber-400 bg-amber-500/10' },
    { type: 'success', icon: Users, text: 'New subscription', user: 'Sarah Banda', time: '5h ago', color: 'text-emerald-400 bg-emerald-500/10' },
    { type: 'info', icon: Activity, text: 'High engagement', user: 'Chemistry Form 4', time: '6h ago', color: 'text-cyan-400 bg-cyan-500/10' },
  ];

  return (
    <div className={`backdrop-blur-sm rounded-2xl border overflow-hidden transition-colors ${
      isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'
    }`}>
      <div className={`p-5 border-b ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Recent Activity</h3>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Live updates from your platform</p>
            </div>
          </div>
          <button className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-100'}`}>
            <MoreVertical className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
        {activities.map((activity, index) => {
          const Icon = activity.icon;
          return (
            <div 
              key={index}
              className={`flex items-start gap-3 p-3 rounded-xl transition-colors group ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg ${activity.color} flex items-center justify-center flex-shrink-0`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>{activity.text}</p>
                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{activity.user}</p>
              </div>
              <div className={`flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                <Clock className="w-3 h-3" />
                <span className="text-xs">{activity.time}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`p-4 border-t ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
        <button className={`w-full py-2.5 text-sm transition-colors flex items-center justify-center gap-2 ${
          isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-800'
        }`}>
          <span>View all activity</span>
          <TrendingUp className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ── Management Card (Users + Subjects tabbed) ──────────────────────

const ManagementCard: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const [tab, setTab] = useState<'users' | 'subjects'>('users');

  return (
    <div className={`backdrop-blur-sm rounded-2xl border overflow-hidden transition-colors ${
      isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200 shadow-sm'
    }`}>
      {/* Tab header */}
      <div className={`p-5 border-b ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab('users')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
              tab === 'users'
                ? 'bg-gradient-to-r from-violet-500 to-purple-500 text-white'
                : isDark ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <Users className="w-3.5 h-3.5" /> Users
          </button>
          <button
            onClick={() => setTab('subjects')}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-medium transition-all ${
              tab === 'subjects'
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                : isDark ? 'text-slate-400 hover:bg-white/5' : 'text-slate-500 hover:bg-slate-50'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" /> Subjects
          </button>
        </div>
      </div>

      {tab === 'users' ? <UsersList isDark={isDark} /> : <SubjectsList isDark={isDark} />}
    </div>
  );
};

// ── Users List ──────────────────────────────────────────────────────

const UsersList: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const [showAddUser, setShowAddUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: users = [], isLoading, error } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: adminService.getUsers
  });

  const filteredUsers = users.filter(user => 
    (user.first_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (user.last_name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (user.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  const deleteUserMutation = useMutation({
    mutationFn: adminService.deleteUser,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['adminUsers'] }),
  });

  return (
    <>
      <div className={`px-5 pt-4 pb-3`}>
        <div className="flex items-center justify-between mb-3">
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{users.length} total users</p>
          <button onClick={() => setShowAddUser(true)} className="p-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 transition-opacity">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm transition-colors focus:outline-none ${
              isDark 
                ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-violet-500/50' 
                : 'bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-violet-400'
            }`}
          />
        </div>
      </div>

      <div className="px-4 pb-4 space-y-2 max-h-[320px] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading users...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="w-10 h-10 text-red-400 mb-2" />
            <p className="text-sm text-red-400">Error loading users</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Users className={`w-10 h-10 mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {searchQuery ? "No users found" : "No users yet"}
            </p>
          </div>
        ) : (
          filteredUsers.map((user, index) => (
            <div 
              key={user.id}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors group ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-semibold ${
                isDark ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-500 to-slate-600'
              }`}>
                {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  {user.first_name} {user.last_name}
                </p>
                <p className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{user.email}</p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => deleteUserMutation.mutate(user.id)}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-red-500/20 text-slate-400 hover:text-red-400' : 'hover:bg-red-50 text-slate-400 hover:text-red-500'}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddUser && (
        <AddUserModal
          isOpen={showAddUser}
          onClose={() => setShowAddUser(false)}
          onSuccess={() => {
            setShowAddUser(false);
            queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
          }}
        />
      )}
    </>
  );
};

// ── Subjects List ───────────────────────────────────────────────────

const SubjectsList: React.FC<{ isDark: boolean }> = ({ isDark }) => {
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: subjects = [], isLoading, error } = useQuery({
    queryKey: ['adminSubjects'],
    queryFn: adminService.getSubjects
  });

  const filteredSubjects = subjects.filter(subject => 
    subject.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{subjects.length} total subjects</p>
          <button onClick={() => setShowAddSubject(true)} className="p-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 transition-opacity">
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search subjects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-10 pr-4 py-2.5 rounded-xl text-sm transition-colors focus:outline-none ${
              isDark 
                ? 'bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:border-amber-500/50' 
                : 'bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-amber-400'
            }`}
          />
        </div>
      </div>

      <div className="px-4 pb-4 space-y-2 max-h-[320px] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-3" />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading subjects...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="w-10 h-10 text-red-400 mb-2" />
            <p className="text-sm text-red-400">Error loading subjects</p>
          </div>
        ) : filteredSubjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <BookOpen className={`w-10 h-10 mb-2 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              {searchQuery ? "No subjects found" : "No subjects yet"}
            </p>
          </div>
        ) : (
          filteredSubjects.map((subject) => (
            <div 
              key={subject.id}
              className={`flex items-center gap-3 p-3 rounded-xl transition-colors group ${
                isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-gradient-to-br from-slate-600 to-slate-700' : 'bg-gradient-to-br from-slate-100 to-slate-200'
              }`}>
                <BookOpen className={`w-4 h-4 ${isDark ? 'text-slate-300' : 'text-slate-500'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>{subject.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  subject.is_archived ? 'bg-slate-500/20 text-slate-400' : 'bg-emerald-500/20 text-emerald-500'
                }`}>
                  {subject.is_archived ? 'Archived' : 'Active'}
                </span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-400 hover:text-white' : 'hover:bg-slate-100 text-slate-400 hover:text-slate-600'}`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={async () => {
                    try {
                      await adminService.deleteSubject(subject.id);
                      queryClient.invalidateQueries({ queryKey: ['adminSubjects'] });
                    } catch (err) { /* handled */ }
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-red-500/20 text-slate-400 hover:text-red-400' : 'hover:bg-red-50 text-slate-400 hover:text-red-500'}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddSubject && (
        <AddSubjectModal
          isOpen={showAddSubject}
          onClose={() => setShowAddSubject(false)}
          onSuccess={() => {
            setShowAddSubject(false);
            queryClient.invalidateQueries({ queryKey: ['adminSubjects'] });
          }}
        />
      )}
    </>
  );
};

export default AdminDashboard;
